<template>
  <section v-if="loading || hasData">
    <page-sub-header title="Coach" :subtitle="isDataReady ? timeSeriesSubtitle : undefined" class="mt-4">
      <template #header>
        <div class="d-flex flex-row align-items-center justify-content-end">
          <app-input
            v-model="timeSeries"
            :options="TIME_SERIES_VALUES"
            type="radio"
            buttons
            button-variant="outline-primary"
            class="text-uppercase mb-0"
            size="sm"
          />
          <export-button
            v-if="isDataReady"
            :file-name="`coach-metrics-${timeSeries}`"
            :export-data="exportData"
            :export-file-rows="exportFileRows"
            :export-headers="exportHeaders"
            class="btn mt-0"
          />
        </div>
      </template>
    </page-sub-header>
    <sme-card v-if="loading">
      <app-loading />
    </sme-card>
    <template v-else-if="hasData">
      <!-- Row 1: Coach Users -->
      <chart-with-companion-kpis
        v-if="timeGroupedCoachUsersDataset.values.length"
        card-class="mb-4"
        chart-title="Coach viewers over time"
        chart-subtitle="Number of employees"
      >
        <line-chart
          :labels="timeGroupDataset"
          :datasets="timeGroupedCoachUsersDataset"
          :chart-options="{
            aspectRatio: getInitialAspectRatio(3, 2),
            scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedCoachUsersDataset.name } } },
            ...createCountWithPercentageTooltip(timeGroupedCoachUsersPercentageDataset),
          }"
          fill-gradient
        />
        <template #companion>
          <div class="stats-combined mb-3">
            <app-stat
              :stat="lastCoachUsers"
              :previous-stat="prevCoachUsers"
              :formatter="toInteger"
              :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Coach Viewers`"
              background-color="core-1-lighten-90"
              color="default"
              pdf-kpi
            />
            <app-stat
              :stat="lastCoachUsersPercentage"
              :previous-stat="prevCoachUsersPercentage"
              :formatter="toNumber"
              suffix="%"
              :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Coach Viewer Percentage`"
              background-color="core-1-lighten-90"
              color="default"
              pdf-kpi
            />
          </div>
          <p class="mb-0">
            {{ timeGroupTitle }}, {{ toInteger(lastCoachUsers) }} employees ({{ lastCoachUsersPercentage }}%) viewed the
            coach.
            {{ coachUsersPrevDiffText }}
          </p>
        </template>
      </chart-with-companion-kpis>

      <!-- Coach Chat Participants (message senders) -->
      <chart-with-companion-kpis
        v-if="timeGroupedCoachChatUsersDataset.values.length"
        card-class="mb-4"
        chart-title="Coach chat participants over time"
        chart-subtitle="Number of employees"
      >
        <line-chart
          :labels="timeGroupDataset"
          :datasets="timeGroupedCoachChatUsersDataset"
          :chart-options="{
            aspectRatio: getInitialAspectRatio(3, 2),
            scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedCoachChatUsersDataset.name } } },
            ...createCountWithPercentageTooltip(timeGroupedCoachChatUsersPercentageDataset),
          }"
          fill-gradient
        />
        <template #companion>
          <div class="stats-combined mb-3">
            <app-stat
              :stat="lastCoachChatUsers"
              :previous-stat="prevCoachChatUsers"
              :formatter="toInteger"
              :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Coach Chat Participants`"
              background-color="core-2-lighten-90"
              color="default"
              pdf-kpi
            />
            <app-stat
              :stat="lastCoachChatUsersPercentage"
              :previous-stat="prevCoachChatUsersPercentage"
              :formatter="toNumber"
              suffix="%"
              :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Chat Participant Percentage`"
              background-color="core-2-lighten-90"
              color="default"
              pdf-kpi
            />
          </div>
          <p class="mb-0">
            {{ timeGroupTitle }}, {{ toInteger(lastCoachChatUsers) }} employees ({{ lastCoachChatUsersPercentage }}%)
            sent messages in the coach chat.
            {{ coachChatUsersPrevDiffText }}
          </p>
        </template>
      </chart-with-companion-kpis>

      <!-- Row 2: Benefits Checker -->
      <chart-with-companion-kpis
        v-if="timeGroupedBenefitsCompletionsDataset.values.length"
        card-class="mb-4"
        chart-title="Benefits checker completions over time"
        chart-subtitle="Number of completions"
      >
        <line-chart
          :labels="timeGroupDataset"
          :datasets="timeGroupedBenefitsCompletionsDataset"
          :chart-options="{
            aspectRatio: getInitialAspectRatio(3, 2),
            scales: {
              y: { ticks: { precision: 0 }, title: { text: timeGroupedBenefitsCompletionsDataset.name } },
            },
          }"
          fill-gradient
        />
        <template #companion>
          <app-stat
            :stat="lastBenefitsCompletions"
            :previous-stat="prevBenefitsCompletions"
            :formatter="toInteger"
            :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Benefits Checker Completions`"
            class="mb-3"
            background-color="core-3-lighten-80"
            color="default"
            pdf-kpi
          />
          <app-stat
            :stat="totalEstimatedBenefits"
            :formatter="value => toInteger(Math.round(value))"
            :prefix="currencySymbol"
            description="Total estimated benefits uncovered"
            background-color="core-3-lighten-90"
            color="default"
            pdf-kpi
          />
          <p v-if="benefitsExplanation" class="mt-2 mb-0 text-muted small">
            {{ benefitsExplanation }}
          </p>
        </template>
      </chart-with-companion-kpis>

      <!-- Row 3: Debt Advice Clicks -->
      <chart-with-companion-kpis
        v-if="timeGroupedDebtCharityDataset.values.length"
        card-class="mb-4"
        chart-title="Debt advice link clicks over time"
        chart-subtitle="Number of clicks"
      >
        <line-chart
          :labels="timeGroupDataset"
          :datasets="timeGroupedDebtCharityDataset"
          :chart-options="{
            aspectRatio: getInitialAspectRatio(3, 2),
            scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedDebtCharityDataset.name } } },
            ...createCountWithPercentageTooltip(timeGroupedDebtCharityPercentageDataset),
          }"
          fill-gradient
        />
        <template #companion>
          <div class="stats-combined mb-3">
            <app-stat
              :stat="lastDebtCharityClicks"
              :previous-stat="prevDebtCharityClicks"
              :formatter="toInteger"
              :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Debt Support Referrals`"
              background-color="core-4-lighten-90"
              color="default"
              pdf-kpi
            />
            <app-stat
              :stat="lastDebtCharityPercentage"
              :previous-stat="prevDebtCharityPercentage"
              :formatter="toNumber"
              suffix="%"
              :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Debt Support Referral Rate`"
              background-color="core-4-lighten-90"
              color="default"
              pdf-kpi
            />
          </div>
          <p class="mb-0">
            {{ timeGroupTitle }}, {{ toInteger(lastDebtCharityClicks) }} employees ({{ lastDebtCharityPercentage }}%)
            were referred to debt support.
            {{ debtCharityPrevDiffText }}
          </p>
        </template>
      </chart-with-companion-kpis>
    </template>
  </section>
</template>

<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue';
import ApiClient from '@/ApiClient';
import { PaletteColors } from '@/Theme';
import AppInput from '@/components/AppInput.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import ChartWithCompanionKpis from '@/components/ChartWithCompanionKpis.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import LineChart from '@/components/charts/LineChart.vue';
import useTimeGroupDataset from '@/composables/useTimeGroupDataset';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import { createCountWithPercentageTooltip, getInitialAspectRatio, TIME_SERIES_VALUES } from '@/utils/chart';
import { currencySymbolFromCode, toInteger, toNumber } from '@/utils/common';
import State from '@/state/State';
import ExportButton from '@/components/ExportButton.vue';

const { divisionId } = useDivisionSelection();

const loading = ref(true);
const timeDataset = ref(null);
const coachUsersDataset = ref({ values: [], name: 'Coach viewers' });
const coachUsersPercentageDataset = ref({ values: [], name: 'Percentage (%)' });
const benefitsCompletionsDataset = ref({ values: [], name: 'Benefits checker completions' });
const entitledCountDataset = ref({ values: [], name: 'Entitled count' });
const estimatedBenefitsDataset = ref({ values: [], name: 'Estimated benefits (£)' });
const debtCharityDataset = ref({ values: [], name: 'Debt charity clicks' });
const debtCharityPercentageDataset = ref({ values: [], name: 'Percentage (%)' });
const coachChatUsersDataset = ref({ values: [], name: 'Coach chat participants' });
const coachChatUsersPercentageDataset = ref({ values: [], name: 'Percentage (%)' });

const {
  timeGroupDataset,
  timeGroupTitle,
  timeSeries,
  timeSeriesUnit,
  timeSeriesSubtitle,
  datasetToTimeGroupedDataset,
  getPrevDiffText,
} = useTimeGroupDataset(timeDataset);

const currencySymbol = computed(() => currencySymbolFromCode(State.state.company.default_currency));

// Coach Users
const timeGroupedCoachUsersDataset = computed(() => datasetToTimeGroupedDataset(coachUsersDataset));
const timeGroupedCoachUsersPercentageDataset = computed(() => datasetToTimeGroupedDataset(coachUsersPercentageDataset));
const lastCoachUsers = computed(() => timeGroupedCoachUsersDataset.value?.calculated.last || 0);
const prevCoachUsers = computed(() => timeGroupedCoachUsersDataset.value?.calculated.prev);
const lastCoachUsersPercentage = computed(() => timeGroupedCoachUsersPercentageDataset.value?.calculated.last || 0);
const prevCoachUsersPercentage = computed(() => timeGroupedCoachUsersPercentageDataset.value?.calculated.prev);
const coachUsersPrevDiffText = computed(() => getPrevDiffText(timeGroupedCoachUsersDataset));

// Coach Chat Participants (actual message senders)
const timeGroupedCoachChatUsersDataset = computed(() => datasetToTimeGroupedDataset(coachChatUsersDataset));
const timeGroupedCoachChatUsersPercentageDataset = computed(() =>
  datasetToTimeGroupedDataset(coachChatUsersPercentageDataset),
);
const lastCoachChatUsers = computed(() => timeGroupedCoachChatUsersDataset.value?.calculated.last || 0);
const prevCoachChatUsers = computed(() => timeGroupedCoachChatUsersDataset.value?.calculated.prev);
const lastCoachChatUsersPercentage = computed(
  () => timeGroupedCoachChatUsersPercentageDataset.value?.calculated.last || 0,
);
const prevCoachChatUsersPercentage = computed(() => timeGroupedCoachChatUsersPercentageDataset.value?.calculated.prev);
const coachChatUsersPrevDiffText = computed(() => getPrevDiffText(timeGroupedCoachChatUsersDataset));

// Benefits Checker
const timeGroupedBenefitsCompletionsDataset = computed(() => datasetToTimeGroupedDataset(benefitsCompletionsDataset));
const timeGroupedEstimatedBenefitsDataset = computed(() => datasetToTimeGroupedDataset(estimatedBenefitsDataset));
const timeGroupedEntitledCountDataset = computed(() => datasetToTimeGroupedDataset(entitledCountDataset));
const lastBenefitsCompletions = computed(() => timeGroupedBenefitsCompletionsDataset.value?.calculated.last || 0);
const prevBenefitsCompletions = computed(() => timeGroupedBenefitsCompletionsDataset.value?.calculated.prev);
const totalEstimatedBenefits = computed(() => {
  const values = timeGroupedEstimatedBenefitsDataset.value?.values || [];
  return values.reduce((sum, val) => sum + (val || 0), 0);
});
const totalCompletions = computed(() => {
  const values = timeGroupedBenefitsCompletionsDataset.value?.values || [];
  return values.reduce((sum, val) => sum + (val || 0), 0);
});
const totalEntitledCount = computed(() => {
  const values = timeGroupedEntitledCountDataset.value?.values || [];
  return values.reduce((sum, val) => sum + (val || 0), 0);
});
const benefitsExplanation = computed(() => {
  if (totalCompletions.value <= 0 || totalEntitledCount.value <= 0) {
    return null;
  }
  const entitledPercentage = Math.round((totalEntitledCount.value / totalCompletions.value) * 100);
  const avgBenefitPerPerson = Math.round(totalEstimatedBenefits.value / totalEntitledCount.value);
  return `${entitledPercentage}% at ${currencySymbol.value}${avgBenefitPerPerson} per month`;
});

// Debt Advice
const timeGroupedDebtCharityDataset = computed(() => datasetToTimeGroupedDataset(debtCharityDataset));
const timeGroupedDebtCharityPercentageDataset = computed(() =>
  datasetToTimeGroupedDataset(debtCharityPercentageDataset),
);
const lastDebtCharityClicks = computed(() => timeGroupedDebtCharityDataset.value?.calculated.last || 0);
const prevDebtCharityClicks = computed(() => timeGroupedDebtCharityDataset.value?.calculated.prev);
const lastDebtCharityPercentage = computed(() => timeGroupedDebtCharityPercentageDataset.value?.calculated.last || 0);
const prevDebtCharityPercentage = computed(() => timeGroupedDebtCharityPercentageDataset.value?.calculated.prev);
const debtCharityPrevDiffText = computed(() => getPrevDiffText(timeGroupedDebtCharityDataset));

const hasData = computed(() => {
  return (
    timeGroupedCoachUsersDataset.value?.values.length > 0 ||
    timeGroupedBenefitsCompletionsDataset.value?.values.length > 0 ||
    timeGroupedDebtCharityDataset.value?.values.length > 0
  );
});

watch([divisionId, timeSeries], () => getCoachMetrics());

const getCoachMetrics = async () => {
  loading.value = true;

  try {
    const [coachUsersResponse, coachChatUsersResponse, benefitsResponse, debtCharityResponse] = await Promise.all([
      ApiClient.getPortalCoachUsers(timeSeries.value, divisionId.value),
      ApiClient.getPortalCoachChatUsers(timeSeries.value, divisionId.value),
      ApiClient.getPortalCoachBenefits(timeSeries.value, divisionId.value),
      ApiClient.getPortalDebtCharity(timeSeries.value, divisionId.value),
    ]);

    // Coach Users - API returns coach_users and enrolled
    let enrolledData = null;
    if (coachUsersResponse?.results?.length) {
      const time = coachUsersResponse.results.find(result => result.id === 'time');
      const coachUsers = coachUsersResponse.results.find(result => result.id === 'coach_users');
      const enrolled = coachUsersResponse.results.find(result => result.id === 'enrolled');

      if (time) {
        timeDataset.value = time;
      }

      if (enrolled) {
        enrolledData = enrolled;
      }

      if (coachUsers) {
        coachUsersDataset.value = {
          ...coachUsers,
          name: 'Coach viewers',
          lineColor: PaletteColors['core-1'],
        };

        // Calculate percentage
        if (enrolled && enrolled.values.length) {
          const percentages = coachUsers.values.map((val, i) => {
            const enrolledCount = enrolled.values[i] || 0;
            return enrolledCount > 0 ? Math.round((val / enrolledCount) * 1000) / 10 : 0;
          });
          coachUsersPercentageDataset.value = {
            values: percentages,
            name: 'Percentage (%)',
            lineColor: PaletteColors['core-1'],
          };
        }
      }
    }

    // Coach Chat Users (actual message senders) - from portal-coach-chat-users endpoint
    if (coachChatUsersResponse?.results?.length) {
      const chatUsers = coachChatUsersResponse.results.find(result => result.id === 'chat_users');

      if (chatUsers) {
        coachChatUsersDataset.value = {
          ...chatUsers,
          name: 'Coach chat participants',
          lineColor: PaletteColors['core-2'],
        };

        // Calculate percentage using enrolled from coach users response
        if (enrolledData && enrolledData.values.length) {
          const percentages = chatUsers.values.map((val, i) => {
            const enrolledCount = enrolledData.values[i] || 0;
            return enrolledCount > 0 ? Math.round((val / enrolledCount) * 1000) / 10 : 0;
          });
          coachChatUsersPercentageDataset.value = {
            values: percentages,
            name: 'Percentage (%)',
            lineColor: PaletteColors['core-2'],
          };
        }
      }
    }

    // Benefits Checker - API returns completions, entitled_count, and estimated_benefits
    if (benefitsResponse?.results?.length) {
      const completions = benefitsResponse.results.find(result => result.id === 'completions');
      const entitledCount = benefitsResponse.results.find(result => result.id === 'entitled_count');
      const estimatedBenefits = benefitsResponse.results.find(result => result.id === 'estimated_benefits');

      if (completions) {
        benefitsCompletionsDataset.value = {
          ...completions,
          name: 'Benefits checker completions',
          lineColor: PaletteColors['core-3'],
        };
      }

      if (entitledCount) {
        entitledCountDataset.value = {
          ...entitledCount,
          name: 'Entitled count',
        };
      }

      if (estimatedBenefits) {
        estimatedBenefitsDataset.value = {
          ...estimatedBenefits,
          name: `Estimated benefits (${currencySymbol.value})`,
          lineColor: PaletteColors['core-3'],
        };
      }
    }

    // Debt Advice - API returns clicks only (use enrolled from coach users)
    if (debtCharityResponse?.results?.length) {
      const clicks = debtCharityResponse.results.find(result => result.id === 'clicks');

      if (clicks) {
        debtCharityDataset.value = {
          ...clicks,
          name: 'Debt advice link clicks',
          lineColor: PaletteColors['core-4'],
        };

        // Calculate percentage using enrolled from coach users response
        if (enrolledData && enrolledData.values.length) {
          const percentages = clicks.values.map((val, i) => {
            const enrolledCount = enrolledData.values[i] || 0;
            return enrolledCount > 0 ? Math.round((val / enrolledCount) * 1000) / 10 : 0;
          });
          debtCharityPercentageDataset.value = {
            values: percentages,
            name: 'Percentage (%)',
            lineColor: PaletteColors['core-4'],
          };
        }
      }
    }
  } catch (error) {
    console.error('Failed to fetch coach metrics:', error);
  }

  loading.value = false;
};

onBeforeMount(() => getCoachMetrics());

const isDataReady = computed(() => {
  return (
    timeGroupDataset.value?.values &&
    coachUsersDataset.value?.values &&
    benefitsCompletionsDataset.value?.values &&
    debtCharityDataset.value?.values &&
    !loading.value
  );
});

const exportData = computed(() => {
  if (!isDataReady.value) {
    return [];
  }

  return timeGroupDataset.value.values.map((time, index) => ({
    time,
    coachViewers: coachUsersDataset.value.values[index] || 0,
    coachViewersPercentage: coachUsersPercentageDataset.value.values[index] || 0,
    coachChatParticipants: coachChatUsersDataset.value.values[index] || 0,
    coachChatParticipantsPercentage: coachChatUsersPercentageDataset.value.values[index] || 0,
    benefitsCompletions: benefitsCompletionsDataset.value.values[index] || 0,
    estimatedBenefits: estimatedBenefitsDataset.value.values[index] || 0,
    debtCharityClicks: debtCharityDataset.value.values[index] || 0,
    debtCharityPercentage: debtCharityPercentageDataset.value.values[index] || 0,
  }));
});

const exportHeaders = computed(() => {
  if (!isDataReady.value) {
    return [];
  }
  return [
    'Date',
    'Coach Viewers',
    'Coach Viewers (%)',
    'Coach Chat Participants',
    'Coach Chat Participants (%)',
    'Benefits Checker Completions',
    `Estimated Benefits (${currencySymbol.value})`,
    'Debt Support Referrals',
    'Debt Support Referral Rate (%)',
  ];
});

const exportFileRows = item => [
  item.time,
  item.coachViewers,
  item.coachViewersPercentage,
  item.coachChatParticipants,
  item.coachChatParticipantsPercentage,
  item.benefitsCompletions,
  Math.round(item.estimatedBenefits || 0),
  item.debtCharityClicks,
  item.debtCharityPercentage,
];
</script>

<style lang="scss" scoped>
.stats-combined {
  display: flex;

  :deep(.app-stat) {
    flex: 1;
    height: auto; // Override height: 100% from AppStat to allow flexbox stretch
    border-radius: 0;
    box-shadow: none;

    &:first-child {
      border-radius: 4px 0 0 4px;
      border-right: 1px solid rgba(0, 0, 0, 0.1);
    }

    &:last-child {
      border-radius: 0 4px 4px 0;
    }

    .app-stat__value-container .app-stat__value {
      font-size: 28px;

      @media (min-width: 992px) {
        font-size: 32px;
      }
    }

    .app-stat__value-container {
      height: auto;
      min-height: 2rem;
    }
  }
}
</style>
